import { Patient } from '../types'
import { BillPayment, BillingAccount, CreditNote, Refund, SalesDocument } from '../types/billing'
import { fromN, toN } from './billingMoney'

export const patientAccountId = (patientId: string) => `acct-pat-${patientId}`

export function patientAccount(p: Patient): BillingAccount {
  return {
    id: patientAccountId(p.id),
    type: 'Patient',
    name: `${p.firstName} ${p.lastName}`.trim(),
    patientId: p.id,
    bmcFileNumber: p.bmcFileNumber,
    phone: p.contact,
    address: p.address,
    status: 'Active',
    createdAt: p.registeredAt,
    createdByName: 'Patient registration',
  }
}

/** Stored accounts plus one virtual account per registered patient — never a duplicate patient record. */
export function mergeAccounts(stored: BillingAccount[], patients: Patient[]) {
  const byId = new Map(stored.map((a) => [a.id, a]))
  patients.forEach((p) => {
    const id = patientAccountId(p.id)
    const existing = byId.get(id)
    // Patient identity always comes from the patient record; billing contact edits are kept.
    byId.set(id, existing ? { ...existing, name: `${p.firstName} ${p.lastName}`.trim(), bmcFileNumber: p.bmcFileNumber } : patientAccount(p))
  })
  return Array.from(byId.values())
}

export type AccountSummary = {
  invoiced: number
  paid: number
  credited: number
  outstanding: number
  overdue: number
  availableCredit: number
  openInvoices: number
}

export function summariseAccount(
  accountId: string,
  documents: SalesDocument[],
  payments: BillPayment[],
  creditNotes: CreditNote[],
  today: string,
): AccountSummary {
  let invoiced = 0
  let outstanding = 0
  let overdue = 0
  let paid = 0
  let credited = 0
  let openInvoices = 0
  for (const d of documents) {
    if (d.accountId !== accountId || d.type !== 'invoice' || d.state !== 'Issued') continue
    invoiced += toN(d.total)
    paid += toN(d.amountPaid)
    credited += toN(d.amountCredited)
    outstanding += toN(d.balance)
    if (toN(d.balance) > 0) {
      openInvoices += 1
      if (d.dueDate && d.dueDate < today) overdue += toN(d.balance)
    }
  }
  const credit =
    payments.filter((p) => p.accountId === accountId && p.state === 'Posted').reduce((s, p) => s + toN(p.unallocated), 0) +
    creditNotes.filter((c) => c.accountId === accountId && c.state === 'Issued').reduce((s, c) => s + toN(c.unallocated), 0)
  return { invoiced: fromN(invoiced), paid: fromN(paid), credited: fromN(credited), outstanding: fromN(outstanding), overdue: fromN(overdue), availableCredit: fromN(credit), openInvoices }
}

export type LedgerRow = {
  date: string
  at: string
  type: 'Invoice' | 'Payment' | 'Deposit' | 'Credit note' | 'Refund' | 'Reversal' | 'Void'
  reference: string
  description: string
  debit: number
  credit: number
  balance: number
  sourceId: string
}

/**
 * Chronological account history. Debits increase what the account owes; credits reduce it.
 * Running balance = invoices − payments − credit notes + refunds (negative = account in credit).
 */
export function accountLedger(
  accountId: string,
  documents: SalesDocument[],
  payments: BillPayment[],
  creditNotes: CreditNote[],
  refunds: Refund[],
  range?: { from?: string; to?: string },
) {
  const rows: Omit<LedgerRow, 'balance'>[] = []
  for (const d of documents) {
    if (d.accountId !== accountId || d.type !== 'invoice' || d.state === 'Draft') continue
    rows.push({ date: d.date, at: d.issuedAt ?? d.createdAt, type: 'Invoice', reference: d.number ?? '', description: d.encounterLabel ?? (d.patientName && d.accountType !== 'Patient' ? `Patient: ${d.patientName}` : 'Invoice'), debit: d.total, credit: 0, sourceId: d.id })
    if (d.state === 'Void') rows.push({ date: (d.voidedAt ?? d.updatedAt).slice(0, 10), at: d.voidedAt ?? d.updatedAt, type: 'Void', reference: d.number ?? '', description: `Voided — ${d.voidReason ?? ''}`, debit: 0, credit: d.total, sourceId: d.id })
  }
  for (const p of payments) {
    if (p.accountId !== accountId) continue
    rows.push({ date: p.date, at: p.createdAt, type: p.kind === 'Deposit' ? 'Deposit' : 'Payment', reference: p.receiptNumber, description: `${p.methodName}${p.reference ? ` · ${p.reference}` : ''}`, debit: 0, credit: p.amount, sourceId: p.id })
    if (p.state === 'Reversed') rows.push({ date: (p.reversedAt ?? p.createdAt).slice(0, 10), at: p.reversedAt ?? p.createdAt, type: 'Reversal', reference: p.receiptNumber, description: `Payment reversed — ${p.reversalReason ?? ''}`, debit: p.amount, credit: 0, sourceId: p.id })
  }
  for (const c of creditNotes) {
    if (c.accountId !== accountId) continue
    rows.push({ date: c.date, at: c.createdAt, type: 'Credit note', reference: c.number, description: c.reason, debit: 0, credit: c.amount, sourceId: c.id })
    if (c.state === 'Void') rows.push({ date: (c.voidedAt ?? c.createdAt).slice(0, 10), at: c.voidedAt ?? c.createdAt, type: 'Void', reference: c.number, description: `Credit note voided — ${c.voidReason ?? ''}`, debit: c.amount, credit: 0, sourceId: c.id })
  }
  for (const r of refunds) {
    if (r.accountId !== accountId) continue
    rows.push({ date: r.date, at: r.createdAt, type: 'Refund', reference: r.number, description: `${r.methodName} — ${r.reason}`, debit: r.amount, credit: 0, sourceId: r.id })
  }
  rows.sort((a, b) => (a.date === b.date ? a.at.localeCompare(b.at) : a.date.localeCompare(b.date)))
  let running = 0
  let opening = 0
  const out: LedgerRow[] = []
  for (const row of rows) {
    running += toN(row.debit) - toN(row.credit)
    if (range?.from && row.date < range.from) {
      opening = running
      continue
    }
    if (range?.to && row.date > range.to) continue
    out.push({ ...row, balance: fromN(running) })
  }
  return { opening: fromN(opening), rows: out, closing: out.length ? out[out.length - 1].balance : fromN(opening) }
}
